import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import ButtonComp from "../../../../../../components/Button";
import { string } from "../../../../../../constants/string";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import { useClaimBenefitStore } from "../../../../../../store/useClaimBenefitStore";

export default function ClaimBenefit(props: any) {
  const { handleBack, handleBenefitNext } = props;

  const setProduct = useClaimFormStore((state) => state.setProduct);
  const setSupplementary = useClaimFormStore((state) => state.setSupplementary);
  const claimBenefit = useClaimBenefitStore((state) => state.claimBenefit);

  const hasCashAndService =
    claimBenefit?.product?.cash_and_service &&
    Object.keys(claimBenefit.product.cash_and_service).length > 0;
  const isOrangeCampaign = Boolean(claimBenefit?.isOrangeCampaign);
  const isOrangeCampaignServicesOnly = Boolean(
    claimBenefit?.isOrangeCampaignServicesOnly,
  );
  const isClaimSubmissionWindowExpired = Boolean(
    claimBenefit?.isClaimSubmissionWindowExpired ??
      claimBenefit?.is_claim_submission_window_expired,
  );

  const [benefitType, setBenefitType] = useState(
    isOrangeCampaignServicesOnly
      ? "Services Only"
      : hasCashAndService
        ? "Service + Cash"
        : "Services Only",
  );
  const [selectedOptions, setSelectedOptions] = useState<{
    [key: string]: string;
  }>({});

  const handleTopupChange = (id: string, value: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleNext = () => {
    let cash = "0";
    let service = "0";

    if (benefitType === "Cash Only") {
      cash = claimBenefit?.product?.cash_only ?? "0";
    } else if (benefitType === "Services Only") {
      service = claimBenefit?.product?.service_only ?? "0";
    } else if (benefitType === "Service + Cash") {
      cash = claimBenefit?.product?.cash_and_service?.cash ?? "0";
      service = claimBenefit?.product?.cash_and_service?.service ?? "0";
    }

    const productData = {
      productId: claimBenefit?.product.product_id,
      productName: claimBenefit?.product.plan_and_product_name,
      isUnderWaitingPeriod: claimBenefit?.product.is_under_waiting_period,
      cashOnly: benefitType === "Cash Only",
      serviceOnly: benefitType === "Services Only",
      cashAndService: benefitType === "Service + Cash",
      cash,
      service,
    };

    setProduct(productData);

    // Supplementary section
    const supplementaryData =
      claimBenefit?.supplementaryData?.map((item: any) => {
        let cash = "0";
        let service = "0";
        const isTopupUnderWaitingPeriod =
          item.is_under_waiting_period === true;
        const selected =
          selectedOptions[item.supplementary_id] || "Cash Payout";

        if (isTopupUnderWaitingPeriod) {
          cash = "0";
          service = "0";
        } else if (selected === "Cash Payout") {
          cash = item.cash_only ?? "0";
        } else if (selected === "Service") {
          service = item.service_only ?? "0";
        }

        return {
          supplementaryId: item.supplementary_id,
          supplementaryName: item.supplementary_name,
          isUnderWaitingPeriod: item.is_under_waiting_period,
          cashOnly: selected === "Cash Payout",
          serviceOnly: selected === "Service",
          cash,
          service,
        };
      }) ?? [];

    setSupplementary(supplementaryData);

    handleBenefitNext();
  };

  useEffect(() => {
    if (isOrangeCampaignServicesOnly) {
      setBenefitType("Services Only");
      return;
    }

    setBenefitType(hasCashAndService ? "Service + Cash" : "Services Only");
  }, [hasCashAndService, isOrangeCampaignServicesOnly]);

  useEffect(() => {
    if (claimBenefit?.supplementaryData) {
      const initialSelections: { [key: string]: string } = {};
      claimBenefit.supplementaryData.forEach((item: any) => {
        initialSelections[item.supplementary_id] = isOrangeCampaignServicesOnly
          ? "Service"
          : "Cash Payout";
      });
      setSelectedOptions(initialSelections);
    }
  }, [claimBenefit, isOrangeCampaignServicesOnly]);

  return (
    <>
      <Box
        textAlign="left"
        width={{ xs: "100%", md: "80%", lg: "100%" }}
        sx={{ alignSelf: "flex-start" }}
      >
        <Box mb={4}>
          <Typography fontWeight={600} fontSize={18} mb={2}>
            Choose the type of benefits
          </Typography>
          {isOrangeCampaign && (
            <Typography color="warning.main" fontSize={14} mb={2}>
              This policy was created under the Orange Campaign.
            </Typography>
          )}
          {isClaimSubmissionWindowExpired && (
            <Box
              sx={{
                border: "1px solid #F44336",
                backgroundColor: "#FFF4F4",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Typography color="error.main" fontSize={15} fontWeight={700}>
                Claim submission window expired
              </Typography>
              <Typography color="text.secondary" fontSize={14} mt={0.5}>
                This claim is outside the allowed 180-day submission window
                from the date of death.
              </Typography>
            </Box>
          )}
          <RadioGroup
            row
            value={benefitType}
            onChange={(e) => setBenefitType(e.target.value)}
          >
            <FormControlLabel
              value="Services Only"
              control={<Radio color="warning" />}
              label="Services Only"
              sx={{
                "& .MuiTypography-root": { fontWeight: 400 },
              }}
            />
            <FormControlLabel
              value="Cash Only"
              control={<Radio color="warning" />}
              label="Cash Only"
              disabled={isOrangeCampaignServicesOnly}
              sx={{
                "& .MuiTypography-root": { fontWeight: 400 },
              }}
            />
            {Object.keys(claimBenefit?.product.cash_and_service).length > 0 && (
              <FormControlLabel
                value="Service + Cash"
                control={<Radio color="warning" />}
                label="Service + Cash"
                disabled={isOrangeCampaignServicesOnly}
                sx={{
                  "& .MuiTypography-root": { fontWeight: 400 },
                }}
              />
            )}
          </RadioGroup>
        </Box>

        {/* TopUps */}
        {claimBenefit?.supplementaryData.length > 0 && (
          <Box mb={4}>
            <Typography fontWeight={600} fontSize={18} mb={2}>
              TopUps
            </Typography>

            {/* One single Paper wrapping all topups */}
            <Box
              sx={{
                border: "2px solid #eee",
                borderRadius: "12px",
                maxHeight: 400,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {claimBenefit?.supplementaryData.map((item: any) => {
                const isTopupUnderWaitingPeriod =
                  item.is_under_waiting_period === true;
                return (
                  <Grid
                    container
                    alignItems="center"
                    key={item.supplementary_id}
                    spacing={1}
                    wrap="wrap"
                  >
                    <Grid size={{ xs: 3 }}>
                      <Typography fontWeight={600}>
                        {item.supplementary_name}
                      </Typography>
                      {isTopupUnderWaitingPeriod && (
                        <Typography color="error.main" fontSize={13}>
                          Top-up is under waiting period; payout will be R0.
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Typography>
                        R
                        {selectedOptions[item.supplementary_id] === "Service"
                          ? item.service_only
                          : item.cash_only}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 7 }}>
                      <RadioGroup
                        row
                        value={
                          selectedOptions[item.supplementary_id] ||
                          "Cash Payout"
                        }
                        onChange={(e) =>
                          handleTopupChange(
                            item.supplementary_id,
                            e.target.value,
                          )
                        }
                        sx={{ flexWrap: "wrap" }}
                      >
                        <FormControlLabel
                          value="Cash Payout"
                          control={<Radio color="warning" />}
                          label="Cash Payout"
                          disabled={isOrangeCampaignServicesOnly}
                          sx={{ mr: 8 }}
                        />
                        <FormControlLabel
                          value="Service"
                          control={<Radio color="warning" />}
                          label="Service"
                        />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          py: 1,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2 },
        }}
      >
        <ButtonComp
          variant="outlined"
          height={40}
          fontSize="16px"
          width={200}
          onClick={handleBack}
        >
          {string.claims.back}
        </ButtonComp>
        <ButtonComp
          variant="contained"
          height={40}
          fontSize="16px"
          width={200}
          onClick={handleNext}
        >
          {string.claims.next}
        </ButtonComp>
      </Box>
    </>
  );
}
